import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  NotificationsActive,
  VerifiedUser,
  PersonOff,
  ContentCopy,
  People,
  Done,
  Close,
} from '@mui/icons-material';
import api from '../../services/api';

interface MigrationStatus {
  totalActiveUsers: number;
  withValidEmail: number;
  pendingEmailUpdate: number;
  emailVerified: number;
  forceEmailUpdate: number;
  duplicateEmails: number;
  progressPercent: number;
  readyToFinalize: boolean;
}

interface PendingUser {
  ID: string;
  USERNAME: string;
  EMAIL: string;
  LAST_LOGIN_TIME: number | null;
  PERFIL: string;
}

interface DuplicateEmail {
  EMAIL: string;
  count: number;
}

interface NotifyUser {
  id: string;
  username: string;
  nome: string;
  telefone: string | null;
  perfil: string;
  lastLogin: string | null;
}

const EmailMigration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<MigrationStatus | null>(null);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateEmail[]>([]);
  const [notifyList, setNotifyList] = useState<NotifyUser[]>([]);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/email-migration/status');
      if (response.data.success) {
        setStatus(response.data.migration);
        setPendingUsers(response.data.pendingUsers || []);
        setDuplicates(response.data.duplicates || []);
      }
    } catch (error) {
      console.error('Error fetching migration status:', error);
      setAlertMessage({ type: 'error', message: 'Erro ao carregar estado da migração' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleNotifyList = async () => {
    setNotifyLoading(true);
    try {
      const response = await api.post('/auth/email-migration/notify', { method: 'list' });
      if (response.data.success) {
        setNotifyList(response.data.users || []);
        setNotifyDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching notify list:', error);
      setAlertMessage({ type: 'error', message: 'Erro ao carregar lista de notificação' });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleSendPush = async () => {
    setNotifyLoading(true);
    try {
      const response = await api.post('/auth/email-migration/notify', { method: 'push' });
      if (response.data.success) {
        setAlertMessage({
          type: 'success',
          message: `${response.data.notified} notificações push enviadas de ${response.data.totalPending} pendentes`,
        });
        setNotifyDialogOpen(false);
      }
    } catch (error) {
      console.error('Error sending push:', error);
      setAlertMessage({ type: 'error', message: 'Erro ao enviar notificações push' });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const response = await api.post('/auth/email-migration/finalize');
      if (response.data.success) {
        setAlertMessage({ type: 'success', message: response.data.message });
        setFinalizeDialogOpen(false);
        fetchStatus();
      }
    } catch (error: any) {
      const details = error.response?.data?.details;
      setAlertMessage({
        type: 'error',
        message: details?.message || 'Erro ao finalizar migração',
      });
    } finally {
      setFinalizing(false);
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Nunca';
    return new Date(timestamp * 1000).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 100) return 'success';
    if (percent >= 75) return 'info';
    if (percent >= 50) return 'warning';
    return 'error';
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Migração Email
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Progresso da migração de autenticação: username para email
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<NotificationsActive />}
            onClick={handleNotifyList}
            disabled={notifyLoading || !status?.pendingEmailUpdate}
          >
            Notificar Pendentes
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchStatus}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<VerifiedUser />}
            onClick={() => setFinalizeDialogOpen(true)}
            disabled={!status?.readyToFinalize}
          >
            Finalizar Migração
          </Button>
        </Box>
      </Box>

      {/* Alert */}
      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          sx={{ mb: 3 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.message}
        </Alert>
      )}

      {/* Progress Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Progresso Geral</Typography>
          <Typography variant="h4" fontWeight={700} color={`${getProgressColor(status?.progressPercent || 0)}.main`}>
            {status?.progressPercent || 0}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={status?.progressPercent || 0}
          color={getProgressColor(status?.progressPercent || 0)}
          sx={{ height: 12, borderRadius: 6, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {status?.withValidEmail || 0} de {status?.totalActiveUsers || 0} utilizadores com email válido
        </Typography>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <People />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{status?.totalActiveUsers || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Ativos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{status?.emailVerified || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Email Verificado</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                  <Warning />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{status?.pendingEmailUpdate || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Sem Email Válido</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: status?.duplicateEmails ? 'error.light' : 'success.light', color: status?.duplicateEmails ? 'error.main' : 'success.main' }}>
                  {status?.duplicateEmails ? <ErrorIcon /> : <Done />}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{status?.duplicateEmails || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Emails Duplicados</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ready to Finalize */}
      {status?.readyToFinalize && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<VerifiedUser />}>
          <AlertTitle>Pronto para finalizar!</AlertTitle>
          Todos os utilizadores têm email válido e não existem duplicados.
          Pode finalizar a migração aplicando o UNIQUE INDEX.
        </Alert>
      )}

      {/* Duplicate Emails Warning */}
      {duplicates.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Emails duplicados encontrados ({duplicates.length})</AlertTitle>
          Clique em "Merge" para manter a conta mais recente e desactivar as duplicadas.
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {duplicates.map((d, i) => (
              <Chip
                key={i}
                label={`${d.EMAIL} (${d.count}x)`}
                color="error"
                variant="outlined"
                size="small"
                onDelete={async () => {
                  if (!window.confirm(`Merge contas com email ${d.EMAIL}? A conta mais recente será mantida.`)) return;
                  try {
                    const resp = await api.post('/auth/email-migration/merge-duplicates', { email: d.EMAIL });
                    if (resp.data.success) {
                      setAlertMessage({ type: 'success', message: `Merge concluído: ${resp.data.details?.join(', ') || 'OK'}` });
                      fetchStatus();
                    }
                  } catch (err: any) {
                    setAlertMessage({ type: 'error', message: err.message });
                  }
                }}
                deleteIcon={<span style={{ fontSize: 10, fontWeight: 700 }}>MERGE</span>}
              />
            ))}
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={async () => {
                if (!window.confirm('Merge TODOS os emails duplicados? Será mantida a conta mais recente de cada.')) return;
                try {
                  const resp = await api.post('/auth/email-migration/merge-duplicates', {});
                  if (resp.data.success) {
                    setAlertMessage({ type: 'success', message: `${resp.data.merged} duplicados resolvidos` });
                    fetchStatus();
                  }
                } catch (err: any) {
                  setAlertMessage({ type: 'error', message: err.message });
                }
              }}
            >
              Merge Todos
            </Button>
          </Box>
        </Alert>
      )}

      {/* Pending Users Table */}
      {pendingUsers.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Utilizadores sem Email Válido ({pendingUsers.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email Atual</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Último Login</TableCell>
                  <TableCell align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.ID} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'warning.main' }}>
                          <PersonOff sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.USERNAME}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={user.EMAIL?.includes('@pending.pagali.cv') ? 'error' : 'text.primary'}
                      >
                        {user.EMAIL?.includes('@pending.pagali.cv') ? (
                          <Chip label="placeholder" size="small" color="error" variant="outlined" />
                        ) : (
                          user.EMAIL || <Chip label="vazio" size="small" color="default" variant="outlined" />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={user.PERFIL || 'N/A'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.LAST_LOGIN_TIME)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="Pendente"
                        size="small"
                        color="warning"
                        icon={<Warning sx={{ fontSize: 14 }} />}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && pendingUsers.length === 0 && status && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Todos os utilizadores têm email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Não existem utilizadores pendentes de atualização de email.
          </Typography>
        </Paper>
      )}

      {/* Notify Dialog */}
      <Dialog open={notifyDialogOpen} onClose={() => setNotifyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive color="primary" />
            <Typography variant="h6">Notificar Utilizadores Pendentes ({notifyList.length})</Typography>
          </Box>
          <IconButton onClick={() => setNotifyDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {notifyList.length === 0 ? (
            <Typography color="text.secondary">Nenhum utilizador pendente.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Último Login</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifyList.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{user.nome || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.username}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.telefone || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.perfil} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : 'Nunca'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={<NotificationsActive />}
            onClick={handleSendPush}
            disabled={notifyLoading || notifyList.length === 0}
          >
            {notifyLoading ? 'A enviar...' : `Enviar Push (${notifyList.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finalize Dialog */}
      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="success" />
            <Typography variant="h6">Finalizar Migração</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Ação irreversível</AlertTitle>
            Esta ação vai:
          </Alert>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">Adicionar UNIQUE INDEX no campo EMAIL</Typography>
            </li>
            <li>
              <Typography variant="body2">Marcar todos os emails como verificados</Typography>
            </li>
            <li>
              <Typography variant="body2">Impedir registos com emails duplicados</Typography>
            </li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Após finalizar, o próximo passo é remover o fallback de username na query de login (manual).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={finalizing ? <CircularProgress size={16} color="inherit" /> : <VerifiedUser />}
            onClick={handleFinalize}
            disabled={finalizing}
          >
            {finalizing ? 'A finalizar...' : 'Confirmar Finalização'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailMigration;
